import React, { createContext, useContext, useMemo } from 'react';
import { AssignEmployeeCallbacks, ChangeZoneCallbacks, ZoneOption, AssigneeOption } from '../fields/types';

interface BaseDashboardFieldsContextType {
  assignCallbacks?: AssignEmployeeCallbacks;
  changeZoneCallbacks?: ChangeZoneCallbacks;
  zoneOptions?: ZoneOption[];
  assigneeOptions?: AssigneeOption[];
  zones?: Record<string, string>;
  currentUserAlias?: string;
  predefinedIssues?: any[];
  customData?: Record<string, any>;
}

type DashboardFieldsContextType<T = BaseDashboardFieldsContextType> = T;

const DashboardFieldsContext = createContext<DashboardFieldsContextType | undefined>(undefined);

export const useDashboardFieldsContext = <T = BaseDashboardFieldsContextType,>() => {
  const context = useContext(DashboardFieldsContext) as DashboardFieldsContextType<T> | undefined;
  if (!context) {
    throw new Error('useDashboardFieldsContext must be used within DashboardFieldsProvider');
  }
  return context;
};

interface DashboardFieldsProviderProps {
  children: React.ReactNode;
  assignCallbacks?: AssignEmployeeCallbacks;
  changeZoneCallbacks?: ChangeZoneCallbacks;
  zoneOptions?: ZoneOption[];
  assigneeOptions?: AssigneeOption[];
  zones?: Record<string, string>;
  currentUserAlias?: string;
  predefinedIssues?: any[];
  customData?: Record<string, any>;
}

export const DashboardFieldsProvider: React.FC<DashboardFieldsProviderProps> = ({
  children,
  assignCallbacks,
  changeZoneCallbacks,
  zoneOptions,
  assigneeOptions,
  zones,
  currentUserAlias,
  predefinedIssues,
  customData,
}) => {
  const value = useMemo(
    () => ({
      assignCallbacks,
      changeZoneCallbacks,
      zoneOptions,
      assigneeOptions,
      zones,
      currentUserAlias,
      predefinedIssues,
      customData,
    }),
    [
      assignCallbacks,
      changeZoneCallbacks,
      zoneOptions,
      assigneeOptions,
      zones,
      currentUserAlias,
      predefinedIssues,
      customData,
    ],
  );

  return <DashboardFieldsContext.Provider value={value}>{children}</DashboardFieldsContext.Provider>;
};
